/**
 * OrganizedTimeline - Hierarchical Task View
 * Example implementation showing how to use HierarchicalView component
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import { useFamilyContext } from '../contexts/FamilyContext';
import { useCollegeContext } from '../contexts/CollegeContext';
import { useTimelineTasks } from '../hooks/useTimelineTasks';
import HierarchicalView from '../components/Timeline/HierarchicalView';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

const OrganizedTimeline = () => {
  const navigate = useNavigate();
  const { currentUser } = useFamilyContext();
  const { colleges } = useCollegeContext();

  // Fetch timeline data using existing hook
  const {
    timeline,
    loading,
    refetch: refetchTimeline
  } = useTimelineTasks(currentUser?.id, colleges);

  // Handle task click - navigate to detail or open modal
  const handleTaskClick = (task) => {
    console.log('Task clicked:', task);

    // Option 1: Navigate based on task type
    if (task.module_link) {
      navigate(task.module_link);
    }

    // Option 2: Open task detail modal
    // setSelectedTask(task);
    // setShowTaskModal(true);
  };

  // Handle task completion
  const handleTaskComplete = async (task) => {
    try {
      // Update task status in database
      const { error } = await supabase
        .from('timeline_items')
        .update({
          status: task.status === 'completed' ? 'planned' : 'completed',
          completion_status: task.status === 'completed' ? 'not_started' : 'completed',
          completed_at: task.status === 'completed' ? null : new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', task.id);

      if (error) throw error;

      toast.success(
        task.status === 'completed'
          ? 'Task marked as incomplete'
          : 'Task completed!'
      );

      // Refresh timeline to show updated progress
      refetchTimeline();

    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  // Handle AI support request
  const handleAISupport = (task) => {
    console.log('AI support requested for:', task);

    // Navigate to AI support with task context
    navigate('/college/ai-support', {
      state: {
        task: task,
        context: 'timeline'
      }
    });

    // Or open AI modal
    // setAISupportTask(task);
    // setShowAIModal(true);
  };

  // Handle focus mode activation (from recovery banner)
  const handleFocusMode = () => {
    // Could navigate to filtered view
    navigate('/college/timeline?filter=overdue');

    // Or activate focus mode in current view
    // setViewMode('focus');
    // setFilterCategory('overdue');
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your organized timeline...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <div className="flex items-center justify-between">
              {/* Left: Back button and title */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/college')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Back to College Hub"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-700" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Organized Timeline
                  </h1>
                  <p className="text-sm text-gray-600">
                    {currentUser?.name || 'Student'} • {colleges?.length || 0} school{colleges?.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {/* Right: Add task button */}
              <button
                onClick={() => navigate('/college/timeline?action=add')}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Task</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <HierarchicalView
          timeline={timeline}
          colleges={colleges}
          onTaskClick={handleTaskClick}
          onTaskComplete={handleTaskComplete}
          onAISupport={handleAISupport}
          onFocusMode={handleFocusMode}
        />
      </div>
    </div>
  );
};

export default OrganizedTimeline;
